/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useContext, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { isValidEmail } from 'utils/helpers';

import { SpecialistContext } from 'context';
import { NotificationContext } from 'context/NotificationContext';
import useRequest from 'hooks/useRequest';
import { setLogin } from 'services/amplify';
import { resetTemporaryPassword } from 'services/specialist/specialist';

import { InputCredentialsProps } from './types';

import * as S from '../styles';

const InputCredentials: React.FC<InputCredentialsProps> = ({
  onSetNewLogin,
  onStateChange,
  onPasswordRecovery,
  onTemporaryPasswordRecovery,
}) => {
  const intl = useIntl();

  const { setAuthenticated } = useContext(SpecialistContext);
  const { setNotification } = useContext(NotificationContext);

  const getTemporaryPasswordRecoverRequest = useRequest();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [temporaryPasswordExpired, setTemporaryPasswordExpired] =
    useState(false);

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (temporaryPasswordExpired) {
      setTemporaryPasswordExpired(false);
    }
  };

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (invalidCredentials) {
      setInvalidCredentials(false);
    }
  };

  const handleTemporaryPasswordRecovery = async () => {
    try {
      getTemporaryPasswordRecoverRequest.setLoading();
      const successResponse = await resetTemporaryPassword(email);
      if (successResponse) {
        getTemporaryPasswordRecoverRequest.setSuccess();
        onTemporaryPasswordRecovery();
      }
    } catch (e) {
      getTemporaryPasswordRecoverRequest.setError(e);
      setNotification({
        message: intl.formatMessage({
          id: 'LOGIN.CREDENTIALS_INPUT.TEMPORARY_PASSWORD_RECOVERY.NOTIFICATION.ERROR',
        }),
        type: 'failure',
      });
    }
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      const response = await setLogin(email, password);

      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        onSetNewLogin(response);
      } else if (response === 'expired') {
        setTemporaryPasswordExpired(true);
        setLoginLoading(false);
      } else {
        setAuthenticated(true);
        onStateChange('signedIn', response);
      }
    } catch (e) {
      setInvalidCredentials(true);
      setLoginLoading(false);
    }
  };

  const handleActionKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keyCode = e.which || e.key;

    if (keyCode === 13 && !buttonDisabled) {
      e.preventDefault();
      handleLogin();
    }
  };

  const cleanEmail = () => {
    if (temporaryPasswordExpired) {
      setTemporaryPasswordExpired(false);
    }
    setEmail('');
  };

  const handleErrorMessage = () => {
    if (invalidCredentials) {
      return intl.formatMessage({
        id: 'LOGIN.CREDENTIALS_INPUT.PASSWORD.ERROR',
      });
    }
    if (temporaryPasswordExpired) {
      return intl.formatMessage({
        id: 'LOGIN.CREDENTIALS_INPUT.PASSWORD.EXPIRED',
      });
    }

    return '';
  };

  useEffect(() => {
    setButtonDisabled(!(isValidEmail(email) && password.length > 5));
  }, [email, password]);

  useEffect(() => {
    return () => {
      setLoginLoading(false);
    };
  }, []);

  return (
    <div data-testid="inputCredentialsStep" onKeyDown={handleActionKeyDown}>
      <S.Title>
        <FormattedMessage id="LOGIN.CREDENTIALS_INPUT.TITLE" />
      </S.Title>

      <S.Form>
        <S.LoginEmailInput
          full
          type="email"
          value={email}
          autoComplete="email"
          onClean={cleanEmail}
          onChange={handleChangeEmail}
          data-testid="inputEmailCredential"
          label={intl.formatMessage({
            id: 'LOGIN.CREDENTIALS_INPUT.EMAIL.LABEL',
          })}
        />
        <S.LoginPasswordInput
          full
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={handleChangePassword}
          data-testid="inputPasswordCredential"
          label={intl.formatMessage({
            id: 'LOGIN.CREDENTIALS_INPUT.PASSWORD.LABEL',
          })}
          error={handleErrorMessage()}
        />
      </S.Form>

      {temporaryPasswordExpired ? (
        <S.TextButton
          onClick={handleTemporaryPasswordRecovery}
          disabled={getTemporaryPasswordRecoverRequest.isLoading()}
          data-testid="temporaryPasswordRecoveryButton"
        >
          <FormattedMessage id="LOGIN.CREDENTIALS_INPUT.TEMPORARY_PASSWORD_RECOVERY" />
        </S.TextButton>
      ) : (
        <S.TextButton
          onClick={onPasswordRecovery}
          data-testid="passwordRecoveryButton"
        >
          <FormattedMessage id="LOGIN.CREDENTIALS_INPUT.PASSWORD_RECOVERY" />
        </S.TextButton>
      )}

      <S.ActionButtonContainer>
        <S.ActionButton
          onClick={handleLogin}
          loading={loginLoading}
          disabled={buttonDisabled}
          data-testid="inputCredentialsActionButton"
        >
          <FormattedMessage id="LOGIN.CREDENTIALS_INPUT.ACTION.ENTER" />
        </S.ActionButton>
      </S.ActionButtonContainer>
    </div>
  );
};

export default InputCredentials;
